import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

import usePopover from "components/common/usePopover";

/**
 * A column-header filter popover for the appointments table.
 *
 * Three shapes behind one control: a static checkbox list (`options`), a
 * SERVER-SEARCHED checkbox list (`loadOptions`), and a date range
 * (`mode="dateRange"`). All commit
 * only on Apply, so a filter is one request rather than one per tick — the list
 * is a paged 9k-row query, so refetching per checkbox is not free.
 *
 * PORTALLED, and that is not a style choice: `.appointments-table` is
 * `overflow-x: auto`, and CSS promotes the other axis to `auto` with it, so an
 * absolutely-positioned menu inside the header is clipped on BOTH axes and
 * becomes unclickable. `usePopover` fixes it to the viewport and clamps it back
 * on screen — which is what keeps the last column's funnel reachable.
 *
 * The working copy is local until Apply: opening, ticking and then dismissing
 * must leave the table exactly as it was.
 */
const FilterGlyph = ({ active }) => (
  <svg
    className={`th-filter-glyph${active ? " is-active" : ""}`}
    viewBox="0 0 12 12"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 2h10l-4 4.2V11L5 9.6V6.2L1 2Z" />
  </svg>
);
FilterGlyph.propTypes = { active: PropTypes.bool };

const Tick = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
    <path d="M3.5 8.4l3 3 6-6.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** The "some but not all" bar, for the Select-all box only. */
const Dash = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
    <path d="M4 8h8" strokeLinecap="round" />
  </svg>
);

const DEBOUNCE_MS = 300;

const ColumnFilter = ({ label, mode, options, loadOptions, value, onApply }) => {
  const [open, setOpen] = useState(false);
  const { triggerRef, menuRef, menuStyle } = usePopover(open, () => setOpen(false));

  /** Selected option ids (checkbox mode). */
  const [picked, setPicked] = useState([]);
  /** {from, to} as yyyy-mm-dd (dateRange mode) — the native input's own format. */
  const [range, setRange] = useState({ from: "", to: "" });
  const [query, setQuery] = useState("");

  /** Server-fetched options (async mode). */
  const [loaded, setLoaded] = useState(null);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);
  const runRef = useRef(0);
  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const active =
    mode === "dateRange"
      ? !!(value && (value.from || value.to))
      : !!(value && value.length);

  // Reseed the working copy from the applied filter on every open, so a
  // dismissed edit is discarded rather than lingering into the next open.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    if (mode === "dateRange") {
      setRange({ from: (value && value.from) || "", to: (value && value.to) || "" });
    } else {
      setPicked(value ? [...value] : []);
    }
  }, [open, mode, value]);

  /**
   * Async options: fetch on open, then per debounced keystroke. The list is
   * server-searched rather than filtered locally — an account list is longer
   * than one page, so a local filter would only ever search what happened to
   * load first.
   */
  useEffect(() => {
    if (!open || !loadOptions) return;
    const run = (runRef.current += 1);
    setBusy(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadOptions(query, (rows) => {
        if (run !== runRef.current) return;
        setLoaded(rows || []);
        setBusy(false);
      });
    }, loaded === null ? 0 : DEBOUNCE_MS);
  }, [open, query, loadOptions, loaded === null]); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    // Async options arrive already matching the query — re-filtering them
    // locally would hide rows the server matched on a field we don't display.
    if (loadOptions) return loaded || [];
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((o) => o.label.toLowerCase().indexOf(q) >= 0);
  }, [options, loaded, loadOptions, query]);

  const toggle = (id) =>
    setPicked((prev) => (prev.indexOf(id) >= 0 ? prev.filter((x) => x !== id) : prev.concat(id)));

  /**
   * Select-all and Invert act on the ROWS CURRENTLY SHOWN, not on the whole
   * dataset. With a search active that is what the user means — selecting after
   * typing "04XX" should take those, not silently pick up 300 codes they never
   * saw. With no search, `shown` IS the whole dataset, so the two coincide.
   * The label says "Select all" either way: the scope is what the operator can
   * see, so it does not need a second name.
   *
   * Bridge expresses this with an include/exclude MODE, because its popover
   * pages 50 rows at a time and can never enumerate all 380. phr's loaders ask
   * for `pageNum: 0, pageSize: 0` (unpaginated — see loadServices), so the full
   * list is always in hand and plain enumeration is exact. Same behaviour, no
   * mode to plumb through the request body.
   */
  const scopeIds = useMemo(() => shown.map((o) => o.id), [shown]);
  const inScope = useMemo(() => {
    const on = scopeIds.filter((id) => picked.indexOf(id) >= 0).length;
    return { on, all: scopeIds.length > 0 && on === scopeIds.length, none: on === 0 };
  }, [scopeIds, picked]);

  const toggleAll = () =>
    setPicked((prev) =>
      // Indeterminate snaps to "all on", which is what a tri-state box does.
      inScope.all
        ? prev.filter((id) => scopeIds.indexOf(id) < 0)
        : prev.concat(scopeIds.filter((id) => prev.indexOf(id) < 0))
    );

  const invert = () =>
    setPicked((prev) =>
      // Ids outside the current scope are left exactly as they were.
      prev
        .filter((id) => scopeIds.indexOf(id) < 0)
        .concat(scopeIds.filter((id) => prev.indexOf(id) < 0))
    );

  const searching = query.trim().length > 0;
  const totalKnown = loadOptions ? (loaded || []).length : (options || []).length;

  // The server 400s on from > to, so it never gets the chance.
  const invalidRange = !!(range.from && range.to && range.from > range.to);
  const commit = (next) => {
    onApply(next);
    setOpen(false);
  };

  const inputRef = useRef(null);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="th-filter"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Filter by ${label}${active ? " (filtered)" : ""}`}
        onClick={() => setOpen((o) => !o)}
      >
        <FilterGlyph active={active} />
      </button>

      {open &&
        ReactDOM.createPortal(
          <div
            ref={menuRef}
            className="cf-pop"
            style={menuStyle}
            role="dialog"
            aria-label={`Filter by ${label}`}
          >
            <div className="cf-head">
              <span className="cf-title">{label}</span>
              {active && (
                <button type="button" className="cf-clear" onClick={() => commit(null)}>
                  Clear
                </button>
              )}
            </div>

            {mode === "dateRange" ? (
              <div className="cf-range">
                {/* Read the value BEFORE the updater runs. React 16 pools
                    synthetic events, so touching `e.target` inside a functional
                    setState — which runs after the handler returns — hits a
                    nullified event and throws, taking the whole page down. */}
                <label className="cf-field">
                  <span>From</span>
                  <input
                    type="date"
                    value={range.from}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRange((r) => ({ ...r, from: v }));
                    }}
                  />
                </label>
                <label className="cf-field">
                  <span>To</span>
                  <input
                    type="date"
                    value={range.to}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRange((r) => ({ ...r, to: v }));
                    }}
                  />
                </label>
                {invalidRange && (
                  <p className="cf-err">&ldquo;From&rdquo; is after &ldquo;To&rdquo;.</p>
                )}
              </div>
            ) : (
              <>
                {(loadOptions || (options || []).length > 8) && (
                  <input
                    ref={inputRef}
                    className="cf-search"
                    type="text"
                    value={query}
                    placeholder={`Search ${label.toLowerCase()}`}
                    aria-label={`Search ${label}`}
                    onChange={(e) => setQuery(e.target.value)}
                  />
                )}
                {shown.length > 0 && (
                  <div className="cf-bulk">
                    <button
                      type="button"
                      className="cf-selectall"
                      aria-pressed={inScope.all}
                      onClick={toggleAll}
                    >
                      <span
                        className={`cf-box${
                          inScope.all ? " is-on" : inScope.none ? "" : " is-mixed"
                        }`}
                        aria-hidden="true"
                      >
                        {inScope.all ? <Tick /> : inScope.none ? null : <Dash />}
                      </span>
                      {/* Always "Select all". The control has only ever acted on
                          the rows currently shown — the label used to rename
                          itself to "Select all matching" mid-search, which read
                          as a second, different control rather than as the same
                          one describing itself. */}
                      <span className="cf-opt-label">Select all</span>
                    </button>
                    <button type="button" className="cf-invert" onClick={invert}>
                      Invert
                    </button>
                  </div>
                )}
                <div className="cf-list" role="group">
                  {busy && shown.length === 0 && <p className="cf-empty">Loading&hellip;</p>}
                  {!busy && shown.length === 0 && <p className="cf-empty">No matches.</p>}
                  {shown.map((o) => {
                    const on = picked.indexOf(o.id) >= 0;
                    return (
                      <button
                        type="button"
                        key={o.id}
                        className="cf-opt"
                        aria-pressed={on}
                        onClick={() => toggle(o.id)}
                      >
                        <span className={`cf-box${on ? " is-on" : ""}`} aria-hidden="true">
                          <Tick />
                        </span>
                        <span className="cf-opt-label">{o.label}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}

            <div className="cf-foot">
              {mode !== "dateRange" && (
                /* Honest about scope: with a search active `totalKnown` is only
                   what matched, and `picked` can hold ids outside it, so
                   "3 of 2 selected" would be a lie. Split the two instead. */
                <span className="cf-count">
                  {searching
                    ? `${picked.length} selected \u00b7 ${shown.length} shown`
                    : `${picked.length} of ${totalKnown} selected`}
                </span>
              )}
              <button type="button" className="cf-cancel" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="cf-apply"
                disabled={mode === "dateRange" && invalidRange}
                onClick={() =>
                  commit(
                    mode === "dateRange"
                      ? range.from || range.to
                        ? range
                        : null
                      : picked.length
                      ? picked
                      : null
                  )
                }
              >
                Apply
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

ColumnFilter.propTypes = {
  label: PropTypes.string.isRequired,
  /** "checkbox" (default) or "dateRange". */
  mode: PropTypes.string,
  /** `(query, cb) => cb([{id,label}])` — server-searched options. */
  loadOptions: PropTypes.func,
  /** Checkbox mode: [{ id, label }]. */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  /** The APPLIED value — an id array, or {from,to} as yyyy-mm-dd. */
  value: PropTypes.any,
  /** Called with the new value, or null to clear. */
  onApply: PropTypes.func.isRequired,
};

export default ColumnFilter;
